// Skills v2 library — Author a skill (2026-08-19). Mockup #s6.
//
// Three stages in one panel: Describe → Review & edit → Publish.
// Publish calls authorSave(body_md) THEN publishSkill(name, {visibility:"tenant",
// data_optional:true}). Every failure (412 draft / 409 name / 422 gate) surfaces
// inline — NEVER alert().

import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { COLORS } from "../../theme/colors";
import { skillsApi } from "../../api/client";
import AttachToAgentModal from "./AttachToAgentModal";
import SkillPreviewImpactPanel from "./SkillPreviewImpactPanel";
import SkillValidationIssues from "./SkillValidationIssues";

const BORDER = COLORS.border || "#e2e5eb";
const MUTED = COLORS.textMuted || "#6b7280";
const DIM = COLORS.textDim || "#9090a8";
const GREEN = COLORS.green || "#10b981";
const RED = COLORS.red || "#ef4444";
const ORANGE = COLORS.orange || "#f59e0b";

const inputStyle = {
  width: "100%", boxSizing: "border-box", fontSize: 13, color: COLORS.text,
  background: COLORS.surfaceHover || COLORS.surface, border: `1px solid ${BORDER}`,
  borderRadius: 8, padding: "8px 11px",
};

// Compose a SKILL.md body from the reviewed draft fields. Kept faithful to the
// draft's own body_md when the user never edits the structured fields; otherwise
// rebuild so review edits are captured. The backend validates (422 on invalid).
function composeBodyMd(draft, steps) {
  const fm = [
    "---",
    `name: ${draft.name || ""}`,
    // version is REQUIRED by the SKILL.md parser — omitting it made the
    // structured-edit path publish an unparseable skill (422).
    `version: ${draft.version || "0.1.0"}`,
    `display_name: ${draft.display_name || ""}`,
    `description: ${draft.description || ""}`,
    "---",
    "",
    `# ${draft.display_name || draft.name || "Skill"}`,
    "",
    "## When to use",
    draft.when_to_use || "",
    "",
    "## Steps",
    ...steps.map((s, i) => `${i + 1}. ${s}`),
    "",
  ];
  return fm.join("\n");
}

export default function AuthorSkillTab({ onPublished, editSkill = null }) {
  const navigate = useNavigate();
  const [attachOpen, setAttachOpen] = useState(false);
  const [loadingEdit, setLoadingEdit] = useState(false);
  // Stage 1
  const [sentence, setSentence] = useState("");
  const [drafting, setDrafting] = useState(false);
  const [draftError, setDraftError] = useState(null);
  // Stage 2
  const [draft, setDraft] = useState(null); // {name, display_name, description, when_to_use, steps, requires, body_md}
  const [steps, setSteps] = useState([]);
  const [rawMode, setRawMode] = useState(false);
  const [rawBody, setRawBody] = useState("");
  const [testNote, setTestNote] = useState(false);
  // Stage 3
  const [publishing, setPublishing] = useState(false);
  const [publishError, setPublishError] = useState(null);
  const [published, setPublished] = useState(false);
  const [issues, setIssues] = useState([]);

  // The body the author is about to save/measure — raw when they're editing raw,
  // otherwise composed from the structured fields.
  const currentBody = draft ? (rawMode ? rawBody : composeBodyMd(draft, steps)) : "";
  const hasErrors = issues.some((i) => i.severity === "error");

  // Live validation (debounced): lint the current SKILL.md so the author sees
  // exactly what to fix as they type, instead of a generic error at publish.
  useEffect(() => {
    if (!draft) { setIssues([]); return undefined; }
    let alive = true;
    const t = setTimeout(async () => {
      try {
        const res = await skillsApi.authorValidate(currentBody);
        const data = (res && res.data && typeof res.data === "object") ? res.data : res;
        if (alive) setIssues(Array.isArray(data?.issues) ? data.issues : []);
      } catch {
        // Validation is advisory — never block the author on its own failure.
        if (alive) setIssues([]);
      }
    }, 400);
    return () => { alive = false; clearTimeout(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentBody, Boolean(draft)]);

  // Edit-existing: load a skill's current body into the editor so the author
  // can tweak it, re-measure, and publish a new version — the edit → measure →
  // publish loop. Opens straight into the raw editor (it's a real SKILL.md).
  useEffect(() => {
    if (!editSkill) return;
    let alive = true;
    setLoadingEdit(true);
    setDraftError(null);
    (async () => {
      try {
        const res = await skillsApi.getBase(editSkill);
        const d = (res && res.data && typeof res.data === "object") ? res.data : res;
        if (!alive || !d) return;
        setDraft({ name: d.name, display_name: d.display_name || d.name, description: d.description || "" });
        setRawBody(d.body_md || "");
        setSteps([]);
        setRawMode(true);
        setPublished(false);
        setPublishError(null);
      } catch (e) {
        if (alive) setDraftError(`Couldn't load "${editSkill}" to edit — ${e?.message || "try again."}`);
      } finally {
        if (alive) setLoadingEdit(false);
      }
    })();
    return () => { alive = false; };
  }, [editSkill]);

  const runDraft = async () => {
    if (!sentence.trim()) {
      setDraftError("Describe the skill in a sentence first.");
      return;
    }
    setDrafting(true);
    setDraftError(null);
    try {
      const res = await skillsApi.authorDraft(sentence.trim());
      const d = (res && res.data && typeof res.data === "object" && !Array.isArray(res.data)) ? res.data : res;
      setDraft(d);
      setSteps(Array.isArray(d?.steps) ? d.steps : []);
      setRawBody(d?.body_md || "");
      setRawMode(false);
      setPublished(false);
      setPublishError(null);
    } catch (e) {
      if (e?.status === 412) {
        setDraftError("Connect a chat model in Admin → API Keys first — the drafting AI needs one.");
      } else {
        setDraftError(e?.message || "Couldn't draft the skill just now.");
      }
    } finally {
      setDrafting(false);
    }
  };

  const publish = async () => {
    if (!draft) return;
    setPublishing(true);
    setPublishError(null);
    try {
      const bodyMd = rawMode ? rawBody : composeBodyMd(draft, steps);
      await skillsApi.authorSave(bodyMd);
      await skillsApi.publishSkill(draft.name, { visibility: "tenant", data_optional: true });
      setPublished(true);
      if (onPublished) onPublished();
    } catch (e) {
      if (e?.status === 409) {
        setPublishError(`That name is taken — ${e?.message || "rename the skill and try again."}`);
      } else if (e?.status === 422) {
        // The eval-gate / invalid-SKILL.md reason is friendly — show it verbatim.
        setPublishError(e?.message || "The skill didn't pass the publish checks.");
      } else {
        setPublishError(e?.message || "Couldn't publish the skill just now.");
      }
    } finally {
      setPublishing(false);
    }
  };

  // Steps editor helpers (mirror OverviewTab's SampleQuestionsEditor).
  const setStep = (i, v) => setSteps(steps.map((s, j) => (j === i ? v : s)));
  const removeStep = (i) => setSteps(steps.filter((_, j) => j !== i));
  const moveStep = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= steps.length) return;
    const next = [...steps];
    [next[i], next[j]] = [next[j], next[i]];
    setSteps(next);
  };
  const addStep = () => setSteps([...steps, ""]);

  return (
    <div style={{ maxWidth: 720 }}>
      <div style={{ marginBottom: 14 }}>
        <h2 style={{ fontSize: 16, margin: 0, color: COLORS.text }}>{editSkill ? "Edit skill" : "Author a skill"}</h2>
        <p style={{ margin: "4px 0 0", color: DIM, fontSize: 13, maxWidth: "66ch" }}>
          {editSkill
            ? "Tweak the skill, measure the impact on a golden set, then publish a new version."
            : "Describe what you want the agent to do — in plain English. We draft the whole skill; you review and publish. No templates, no code, no YAML."}
        </p>
      </div>

      {editSkill && loadingEdit && (
        <div style={{ fontSize: 13, color: DIM }}>Loading “{editSkill}” to edit…</div>
      )}
      {editSkill && draftError && (
        <div style={{ fontSize: 12.5, color: RED, marginBottom: 10 }}>{draftError}</div>
      )}

      {/* Stage 1 — Describe (new skills only; hidden when editing an existing one
          so "Draft it for me" can't overwrite the loaded body). */}
      {!editSkill && (
      <div style={{ border: `1px solid ${BORDER}`, borderRadius: 14, background: COLORS.surface, padding: "14px 16px" }}>
        <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.04em", textTransform: "uppercase", color: DIM, marginBottom: 8 }}>
          ✦ Describe the skill in a sentence
        </div>
        <textarea
          aria-label="Describe the skill"
          value={sentence}
          onChange={(e) => setSentence(e.target.value)}
          rows={2}
          placeholder="When a customer is angry about a late delivery, apologize, offer the 10% goodwill credit, and open a ticket."
          style={{ ...inputStyle, resize: "vertical", minHeight: 56 }}
        />
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 10 }}>
          <button
            type="button"
            onClick={runDraft}
            disabled={drafting}
            style={{
              background: COLORS.accent, color: "#fff", border: "none", borderRadius: 9,
              padding: "9px 15px", fontSize: 13, fontWeight: 700,
              cursor: drafting ? "default" : "pointer", opacity: drafting ? 0.6 : 1,
            }}
          >{drafting ? "Drafting…" : "✦ Draft it for me"}</button>
          <span style={{ fontSize: 12, color: DIM }}>Drafted by your Agent Builder AI in a few seconds.</span>
        </div>
        {draftError && <div style={{ fontSize: 12.5, color: RED, marginTop: 10 }}>{draftError}</div>}
      </div>
      )}

      {/* Stage 2 — Review & edit */}
      {draft && (
        <div style={{
          border: `1px dashed ${COLORS.accent}`, borderRadius: 16,
          background: COLORS.accentSoft || "transparent", padding: "16px 18px", marginTop: 16,
        }}>
          <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: "0.05em", textTransform: "uppercase", color: COLORS.accentText || COLORS.accent, marginBottom: 10 }}>
            ✦ AI draft — review &amp; edit
          </div>

          <label style={{ fontSize: 11, color: DIM }}>Name</label>
          <input
            aria-label="Skill name"
            value={draft.display_name || ""}
            onChange={(e) => setDraft({ ...draft, display_name: e.target.value })}
            style={{ ...inputStyle, marginTop: 4, marginBottom: 10 }}
          />

          <label style={{ fontSize: 11, color: DIM }}>Description</label>
          <textarea
            aria-label="Skill description"
            value={draft.description || ""}
            onChange={(e) => setDraft({ ...draft, description: e.target.value })}
            rows={2}
            style={{ ...inputStyle, marginTop: 4, marginBottom: 10, resize: "vertical" }}
          />

          <label style={{ fontSize: 11, color: DIM }}>When to use</label>
          <input
            aria-label="When to use"
            value={draft.when_to_use || ""}
            onChange={(e) => setDraft({ ...draft, when_to_use: e.target.value })}
            style={{ ...inputStyle, marginTop: 4, marginBottom: 10 }}
          />

          <div style={{ fontSize: 11, color: DIM, marginBottom: 4 }}>Steps</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {steps.map((s, i) => (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <span style={{ fontSize: 12, color: DIM, width: 16 }}>{i + 1}.</span>
                <input
                  aria-label={`Step ${i + 1}`}
                  value={s}
                  onChange={(e) => setStep(i, e.target.value)}
                  style={{ ...inputStyle, flex: 1 }}
                />
                <button type="button" aria-label={`Move step ${i + 1} up`} onClick={() => moveStep(i, -1)} style={miniBtn}>↑</button>
                <button type="button" aria-label={`Move step ${i + 1} down`} onClick={() => moveStep(i, 1)} style={miniBtn}>↓</button>
                <button type="button" aria-label={`Remove step ${i + 1}`} onClick={() => removeStep(i)} style={{ ...miniBtn, color: COLORS.pink || RED }}>×</button>
              </div>
            ))}
            <button
              type="button"
              onClick={addStep}
              style={{ alignSelf: "flex-start", background: "transparent", border: `1px dashed ${BORDER}`, color: MUTED, padding: "4px 10px", borderRadius: 6, fontSize: 11, cursor: "pointer" }}
            >+ Add step</button>
          </div>

          {/* Needs chips */}
          {Array.isArray(draft.requires) && draft.requires.length > 0 && (
            <div style={{ marginTop: 12 }}>
              <div style={{ fontSize: 11, color: DIM, marginBottom: 6 }}>Needs</div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                {draft.requires.map((req, i) => (
                  <span key={i} style={{
                    display: "inline-flex", alignItems: "center", gap: 6, fontSize: 11.5, fontWeight: 650,
                    padding: "5px 10px", borderRadius: 999, color: ORANGE,
                    background: `${ORANGE}1F`, border: `1px solid ${ORANGE}66`,
                  }}>⚠ {req.label || req.declare}</span>
                ))}
              </div>
            </div>
          )}

          {/* Raw body editor */}
          {rawMode && (
            <textarea
              aria-label="Raw SKILL.md"
              value={rawBody}
              onChange={(e) => setRawBody(e.target.value)}
              rows={10}
              style={{ ...inputStyle, marginTop: 12, fontFamily: "monospace", fontSize: 11.5, resize: "vertical" }}
            />
          )}

          {/* Live "what to fix" — parsing errors (block publish) + advisories. */}
          <SkillValidationIssues issues={issues} />

          {/* Measure this draft on a golden set BEFORE publishing (parity with
              prompt tweaking). Injects the current editor body transiently. */}
          <SkillPreviewImpactPanel getBody={() => currentBody} />

          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 14 }}>
            <button
              type="button"
              onClick={() => setTestNote((v) => !v)}
              style={ghostBtn}
            >How testing works</button>
            <button
              type="button"
              onClick={() => setRawMode((v) => !v)}
              style={ghostBtn}
            >{rawMode ? "Hide raw" : "Edit raw"}</button>
            <button
              type="button"
              onClick={publish}
              disabled={publishing || hasErrors}
              title={hasErrors ? "Fix the errors above before publishing" : undefined}
              style={{
                background: COLORS.accent, color: "#fff", border: "none", borderRadius: 9,
                padding: "9px 15px", fontSize: 13, fontWeight: 700,
                cursor: (publishing || hasErrors) ? "default" : "pointer", opacity: (publishing || hasErrors) ? 0.6 : 1,
              }}
            >{publishing ? "Publishing…" : (editSkill ? "Publish new version" : "Publish to my library")}</button>
          </div>

          {testNote && (
            <div style={{ fontSize: 12.5, color: MUTED, marginTop: 10 }}>
              <b>Preview impact</b> (above) runs a paired eval on your golden set — baseline vs
              with this skill — injecting the current draft transiently, nothing saved. Publish is
              itself eval-gated: a data skill won't publish unless it measurably lifts.
            </div>
          )}

          {publishError && <div style={{ fontSize: 12.5, color: RED, marginTop: 10 }}>{publishError}</div>}

          {published && (
            <div style={{
              marginTop: 12, padding: "13px 15px", borderRadius: 12,
              background: `${GREEN}1F`, border: `1px solid ${GREEN}66`, fontSize: 12.5, color: COLORS.text,
            }}>
              <b>Published to your library ✓</b> — now put it to work.
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 10 }}>
                <button
                  type="button"
                  onClick={() => setAttachOpen(true)}
                  style={{
                    background: COLORS.accent, color: "#fff", border: "none", borderRadius: 9,
                    padding: "8px 14px", fontSize: 12.5, fontWeight: 700, cursor: "pointer",
                    boxShadow: `0 0 16px ${COLORS.accentGlow}`,
                  }}
                >＋ Attach to an agent now</button>
                <button
                  type="button"
                  onClick={() => navigate("/skills?tab=gallery")}
                  style={{ background: COLORS.surface, color: MUTED, border: `1px solid ${BORDER}`, borderRadius: 9, padding: "8px 14px", fontSize: 12.5, fontWeight: 600, cursor: "pointer" }}
                >View in the Gallery →</button>
              </div>
            </div>
          )}

          {attachOpen && draft && (
            <AttachToAgentModal
              skillName={draft.name}
              displayName={draft.display_name || draft.name}
              onClose={() => setAttachOpen(false)}
              onAttached={(agentId) => { setAttachOpen(false); navigate(`/library/${agentId}`); }}
            />
          )}
        </div>
      )}

      <div style={{
        display: "flex", gap: 10, alignItems: "flex-start", marginTop: 16, padding: "11px 14px",
        borderRadius: 12, background: COLORS.accentSoft || "transparent",
        border: `1px solid ${COLORS.accent}`, fontSize: 12.5, color: MUTED,
      }}>
        <span>🛡️</span>
        <div>
          Published skills ride your existing controls — role-gated, tenant-scoped, audited,
          and high-value actions still pass through approval.
        </div>
      </div>
    </div>
  );
}

const miniBtn = {
  background: "transparent", border: `1px solid ${BORDER}`, color: DIM,
  width: 24, height: 24, borderRadius: 4, fontSize: 12, cursor: "pointer", lineHeight: 1,
};
const ghostBtn = {
  background: COLORS.surface, border: `1px solid ${BORDER}`, color: MUTED,
  padding: "9px 14px", borderRadius: 9, fontSize: 12.5, fontWeight: 600, cursor: "pointer",
};
